import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

const DEFAULT_SETTINGS = {
    theme: 'white', // white, sepia, black
    fontSize: 18,
    lineHeight: 1.8,
    fontFamily: 'KoPub Batang',
    scrollSpeed: 1, // for auto-scroll
    autoPlay: false
};

let debounceTimer = null;

export const ViewerSettingsService = {
    /**
     * Load settings for a user.
     * Strategy: Cache-first (localStorage), then Network (Firestore).
     * @param {string} uid 
     * @returns {Promise<Object>} The settings object.
     */
    loadSettings: async (uid) => {
        // 1. Try LocalStorage
        const localKey = `viewer_settings_${uid}`;
        const localData = localStorage.getItem(localKey);
        let settings = { ...DEFAULT_SETTINGS };

        if (localData) {
            try {
                settings = { ...settings, ...JSON.parse(localData) };
            } catch (e) { console.error("Settings Parse Error", e); }
        }

        // 2. Fetch from Firestore (Background Sync)
        // We return local immediately mostly, but if we want to wait for "true" sync, we can.
        // For a viewer, instant load is better. We can update state later if needed (but pure JS services usually just return).
        // Here we'll return a promise that resolves with the most likely correct data.

        if (uid) {
            // Non-blocking fetch to update cache for next time? 
            // Or await it? Awaiting adds latency to the viewer opening.
            // Let's await it but with a short timeout race or rely on local if available?
            // User preference: "Sync across devices". This means Firestore is authority.
            // If local exists, it might be stale.
            // Strategy: Return merged.
            try {
                const docRef = doc(db, "users", uid, "settings", "viewer");
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    const remoteStore = snap.data();
                    settings = { ...settings, ...remoteStore };
                    // Update local to match remote
                    localStorage.setItem(localKey, JSON.stringify(settings));
                }
            } catch (e) {
                console.warn("Settings Sync Failed", e);
            }
        }

        return settings;
    },

    /**
     * Save settings locally and sync to Firestore.
     * @param {string} uid 
     * @param {Object} newSettings 
     */
    saveSettings: (uid, newSettings) => {
        if (!uid) return;

        // 1. Local Update
        const localKey = `viewer_settings_${uid}`;
        const current = JSON.parse(localStorage.getItem(localKey) || "{}");
        const merged = { ...DEFAULT_SETTINGS, ...current, ...newSettings };

        localStorage.setItem(localKey, JSON.stringify(merged));

        // 2. Debounced Remote Update
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
            try {
                const docRef = doc(db, "users", uid, "settings", "viewer");
                await setDoc(docRef, merged, { merge: true });
                console.log("[ViewerSettings] Synced to Firestore");
            } catch (e) {
                console.error("[ViewerSettings] Sync Failed", e);
            }
        }, 2000); // 2 second debounce
    },

    /**
     * Get CSS variables for a theme.
     * @param {string} themeName 
     */
    getThemeStyles: (themeName) => {
        switch (themeName) {
            case 'black':
                return { bg: '#000000', text: '#e5e5e5', ui: '#222' };
            case 'sepia':
                return { bg: '#f4ecd8', text: '#5b4636', ui: '#e9e0c9' };
            default: // white
                return { bg: '#ffffff', text: '#111111', ui: '#f8f9fa' };
        }
    }
};
